import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { assert } from 'src/app/core/assert/assert';
import { BaseCalendarModel } from '../../../../../core/models/calendar/base-calendar.model';
import { DayOrWeekCalendarModel } from '../../../../../core/models/calendar/day-or-week-calendar.model';
import { MonthCalendarModel } from '../../../../../core/models/calendar/month-calendar.model';
import { TimeBlockModel } from '../../../../../core/models/timeblock/time-block.model';
import { TrackerResponseModel } from '../../../../../core/models/tracker/tracker-response.model';
import { SharedClockHandlerService } from '../../../../../core/services/features/time-tracking/shared-clock-handler.service';
import {
  SharedTrackerHandlerService,
  TrackerStatus,
} from '../../../../../core/services/features/time-tracking/shared-tracker-handler.service';
import * as fromTimerActions from '../../../../../core/state/time-blocks/timer/timer.actions';
import { CalendarEvents, CalendarViews, TimerStatus } from '../../../calendar-types';
import { CalendarService } from '../../../services/calendar.service';
import { TimeBlockCrudService } from '../crud/time-block-crud.service';
import { TimeBlockItemBuilderService } from '../generation/time-block-item-builder.service';
import { TimeBlockDragResizeControllerService } from '../interaction/movement/drag-and-resize/time-block-drag-resize-handling/time-block-drag-resize-controller.service';
import { TimeBlockStructureService } from '../time-block-structure/time-block-structure.service';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { TimeBlockContentType } from '../types/time-block-types';
import { IActiveTimeBlock } from './active-time-block-interfaces';
import { SharedActiveTimeBlockService } from './shared-active-time-block.service';

@Injectable()
export class ActiveTimeBlockTrackerService implements IActiveTimeBlock {
  private trackerResponseSub$ = null;

  constructor(
    private readonly sharedTrackerHandlerService: SharedTrackerHandlerService,
    private readonly sharedActiveTimeBlockService: SharedActiveTimeBlockService,
    private readonly timeBlockStructureService: TimeBlockStructureService,
    private readonly timeBlockItemBuilderService: TimeBlockItemBuilderService,
    private readonly timeBlockCrudService: TimeBlockCrudService,
    private readonly timeBlockDragResizeControllerService: TimeBlockDragResizeControllerService,
    private readonly calendarService: CalendarService,
    private readonly sharedClockHandlerService: SharedClockHandlerService,
    private readonly store: Store,
  ) {}

  public registerTimeTracking(): void {
    // When time blocks are reloaded, also this method gets called again. So check if a tracker response sub is present.
    if (this.trackerResponseSub$) {
      return;
    }

    this.trackerResponseSub$ = this.sharedTrackerHandlerService.trackerResponse$
      .pipe(filter((response) => response !== null))
      .subscribe((trackerResponseModel) => {
        this.processTrackerResponse(trackerResponseModel);
      });
  }

  public deregisterTimeTracking(): void {
    this.sharedTrackerHandlerService.deregisterTimeTracking();
    if (this.trackerResponseSub$) {
      this.trackerResponseSub$.unsubscribe();
      this.trackerResponseSub$ = null;
    }
  }

  public insertActiveTimeBlock(generatedTimeBlock: ITimeBlockComponentItem): number {
    return this.sharedActiveTimeBlockService.insertActiveTimeBlock(generatedTimeBlock);
  }

  public updateActiveTimeBlock(activeTimeBlock: ITimeBlockComponentItem): void {
    if (!this.sharedActiveTimeBlockService.canUpdate(activeTimeBlock.id)) {
      return;
    }

    // Update end date and replace the time block in the calendar model
    this.sharedActiveTimeBlockService.updateActiveTimeBlock(
      activeTimeBlock.timeBlockModel.id,
      activeTimeBlock,
    );
  }

  /**
   * Update the calendar model with tracker active flag if Shive Tracker is active.
   */
  public toggleTrackerAlive(isShiveTrackerActive: boolean): void {
    const calendarModel = this.calendarService.model;
    let updatedCalendarModel: BaseCalendarModel;
    if (
      calendarModel.calendarViewMode === CalendarViews.DayGrid ||
      calendarModel.calendarViewMode === CalendarViews.WeekGrid
    ) {
      updatedCalendarModel = new DayOrWeekCalendarModel();
    } else {
      // Month view
      updatedCalendarModel = new MonthCalendarModel();
    }
    Object.assign(updatedCalendarModel, this.calendarService.model);
    updatedCalendarModel.shiveTrackerActive = isShiveTrackerActive;

    this.calendarService.emitCalendarChange(
      updatedCalendarModel,
      CalendarEvents.UpdatedShiveTrackerStatus,
    );
  }

  private processTrackerResponse(trackerResponseModel: TrackerResponseModel): void {
    const trackerStatus = this.sharedTrackerHandlerService.trackerStatus$.value;
    // Nothing to do -> return
    if (
      trackerStatus === TrackerStatus.None &&
      this.sharedTrackerHandlerService.trackerTimeBlockComponent$.value === null
    ) {
      return;
    }

    const timeBlockModel = trackerResponseModel.timeBlockModel;

    if (timeBlockModel) {
      assert(timeBlockModel.isActive);
    }

    // We got a time block model from the tracker, no interaction is currently in progress and the tracker time block was not previously
    // inserted into the calendar -> Add it to the calendar.
    const timeBlockFromTrackerExists =
      timeBlockModel !== null &&
      !this.timeBlockDragResizeControllerService.dragOrResizeActionInProgress &&
      !this.timeBlockStructureService.retrieveTimeBlockPart(timeBlockModel.id);

    // Wait until the time block map is initialized.
    if (
      !this.calendarService.model.timeBlocks ||
      this.calendarService.model.timeBlocks.size === 0
    ) {
      return;
    }

    if (timeBlockFromTrackerExists) {
      this.insertTimeBlock(timeBlockModel);
    } else if (this.isUpdatingTimeBlock(timeBlockModel)) {
      this.updateTimeBlock(timeBlockModel);
    } else if (this.isStoppingTimeBlock(trackerStatus)) {
      this.stopTimeBlock();
    }
  }

  private isUpdatingTimeBlock(timeBlockModel: TimeBlockModel): boolean {
    const originalTimeBlockModel =
      this.sharedTrackerHandlerService.trackerTimeBlockComponent$.value;

    return (
      timeBlockModel &&
      originalTimeBlockModel &&
      this.sharedTrackerHandlerService.trackerStatus$.value === TrackerStatus.Recording
    );
  }

  private isStoppingTimeBlock(trackerStatus: TrackerStatus): boolean {
    // We have an active time block here since we checked this beforehand.
    return trackerStatus === TrackerStatus.Pause || trackerStatus === TrackerStatus.None;
  }

  private insertTimeBlock(timeBlockModel: TimeBlockModel): void {
    // If timer switches to tracker, reset clock start.
    this.sharedClockHandlerService.setClockStart = timeBlockModel.start.getTime();

    const activeTimeBlock = this.timeBlockItemBuilderService.buildFromExisting(
      timeBlockModel,
      TimeBlockContentType.Project,
    );
    this.insertActiveTimeBlock(activeTimeBlock);
    this.sharedTrackerHandlerService.trackerTimeBlockComponent$.next(
      activeTimeBlock.timeBlockModel,
    );

    const activeTimerTimeBlockActiveId =
      this.sharedActiveTimeBlockService.timerActiveTBSubject.value;

    // If an active timer time block is present, skip the if clause and stop the timer time block.
    // If the tracker switches from "Pause" to "Start" or from "None" to "Start", don't stop any unexisting timer time block.
    if (
      !activeTimerTimeBlockActiveId ||
      activeTimerTimeBlockActiveId === SharedActiveTimeBlockService.noActiveTimerTimeBlockId
    ) {
      return;
    }

    // Replace active time block and de-register timer.
    this.store.dispatch(
      fromTimerActions.timerPosted({
        payload: {
          status: TimerStatus.Stop,
          generatedTimeBlock: null,
        },
      }),
    );
  }

  private updateTimeBlock(timeBlockModel: TimeBlockModel) {
    const activeTimeBlock = this.timeBlockItemBuilderService.buildFromExisting(
      timeBlockModel,
      TimeBlockContentType.Project,
    );

    this.updateActiveTimeBlock(activeTimeBlock);
  }

  private stopTimeBlock(): void {
    // Time blocks have already been loaded in last iteration, so nothing to do.
    if (this.sharedTrackerHandlerService.trackerTimeBlockComponent$.value === null) {
      return;
    }

    this.timeBlockCrudService.reloadTimeBlocks$.next(true);
    this.sharedTrackerHandlerService.trackerTimeBlockComponent$.next(null);
  }
}
